import { useCallback } from 'react'

import { appToaster } from '~/libs/toaster'

/**
 * 成功 toast のメッセージ。
 *
 * 全消費サイト調査 (13 ファイル) の結果、 title のみ / description のみ / 両方
 * 指定の 3 パターンが実在した (例: `AdminWeatherForecastPoints` は description
 * のみ、 `ProcurementPlanList` は title + description)。 位置引数
 * `(title, description?)` にすると description のみの site で第 1 引数に
 * `undefined` を明示的に渡す必要があり意図が読みにくくなるため、 両方省略可能な
 * object 引数を採用する (= toast の表示され方 (title/description の出し分け) を
 * 変えないための設計判断)。
 */
export type ToastMessage = {
  /** 同じ id の toast が表示中なら、 2 件目を足さず 1 件目を更新する (`appToaster.create` の `id`)。 */
  id?: string
  title?: string
  description?: string
  /** 自動で閉じるまでの ms (省略時は DS/chakra 既定の 5000ms)。 */
  duration?: number
  /** 閉じる (×) ボタンを出すか (**省略時は表示**。 進捗 toast 等で消させたくない時だけ false)。 */
  closable?: boolean
}

/**
 * 成功 toast を表示する hook (`docs/guides/frontend-coding-guide.yaml` の
 * `useSuccessToast()`)。
 *
 * `~/libs/toaster.ts` の appToaster (= ecloud toaster の `as unknown as` キャスト
 * 集約点) を消費する薄い wrapper。 各ページ / hook が同じ型エイリアス
 * (`XxxToaster` 等) と局所キャストを重複定義するのを廃止し、 キャストを
 * libs/toaster.ts の 1 箇所に保つ。
 *
 * 戻り値の関数は module-level singleton の appToaster のみに依存するため
 * `useCallback` の依存配列は空にする (= 移行前の module-level
 * `const toaster = rawToaster as unknown as XxxToaster` と同じ参照安定性を保ち、
 * 呼び出し元が `useCallback` の依存配列に含めても再レンダーのたびに
 * 新しい関数を生成しない)。
 */
export function useSuccessToast(): (message: ToastMessage) => void {
  return useCallback((message: ToastMessage) => {
    appToaster.create({
      /*
       * × は**既定で出す** (デザイナー判断 2026-08-28、 issue #6689)。
       * **この根拠は `useErrorToast` と共通の正本**。 変更する時は両方の既定を
       * 揃えること (向こうはここへ参照させている)。
       * design 側は `isClosable: true` が 197 箇所 / `false` が 1 箇所で、
       * 実質「既定 true」。 実装は 75 箇所中 2 箇所しか渡しておらず、 5 秒の
       * 自動消灯を待つしかない状態だった (説明文付きの toast は読み切る前に
       * 消えうる)。
       *
       * spread の**前**に置くのが要点。 呼び出し側が `closable: false` を渡せば
       * 上書きできる。 opt-out するのは design で唯一 false だったのと同じ
       * 「処理中を知らせる進捗 toast」 (まだ終わっていない処理を消せると
       * 状態が読めなくなる) を想定している。
       */
      closable: true,
      type: 'success',
      ...message,
    })
  }, [])
}
